import {
  createContext,
  type ReactElement,
  type ReactNode,
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { DialogMarshal } from '@infinex/ui/components';

export type DialogService = {
  showDialog: (component: ReactElement) => void;
  hideDialog: () => void;
  dialogState: { isOpen: boolean };
};
const DialogServiceContext = createContext<DialogService | null>(null);

/** Test-only handle — production code must use useDialogService(). */
export const DialogServiceContextForTests = DialogServiceContext;

/** Provides dialog management for unique scenarios within the application. */
export function DialogServiceProvider({ children }: { children: ReactNode }) {
  const raf = useRef<number | null>(null);
  const [dialogElement, setDialogElement] = useState<ReactElement | null>(null);

  const hideDialog = useCallback(() => setDialogElement(null), []);
  const showDialog = useCallback(
    (component: ReactElement) => {
      // close any existing dialog
      hideDialog();

      // wait on next frame to open the requested dialog
      raf.current = requestAnimationFrame(() => {
        setDialogElement(component);
      });
    },
    [hideDialog]
  );

  useLayoutEffect(() => {
    if (raf.current && !dialogElement) {
      cancelAnimationFrame(raf.current);
    }
  }, [dialogElement]);

  return (
    <DialogServiceContext.Provider
      value={{
        showDialog,
        hideDialog,
        dialogState: { isOpen: !!dialogElement },
      }}
    >
      {children}
      <DialogMarshal onDismiss={hideDialog}>{dialogElement}</DialogMarshal>
    </DialogServiceContext.Provider>
  );
}

/**
 * Dialog management for unique scenarios within the application. Usage is not
 * recommended, but required for situations where rendering is not possible,
 * e.g. within _some_ React hooks.
 *
 * @note You probably don't need this. In most cases use `DialogMarshal` or `DialogTrigger` instead.
 *
 * @example
 * ```tsx
 * function useTurnstileDialog() {
 *   const { showDialog } = useDialogService();
 *   return new Promise((resolve) => {
 *     showDialog(
 *       <TurnstileDialog
 *         action="registration"
 *         onVerify={() => resolve(true)}
 *         onDismiss={() => resolve(false)}
 *       />
 *     );
 *   });
 * }
 * ```
 */
export const useDialogService = () => {
  const context = useContext(DialogServiceContext);
  if (!context) {
    throw new Error(
      'The `useDialogService` hook is inaccessible. Ensure that `DialogServiceProvider` is present in the root of the component tree.'
    );
  }
  return context;
};
